<template>
  <div v-show="isOpen" class="archive-search-modal-overlay" @click.self="close">
    <div class="archive-search-modal-content">
      <h2 class="sr-only">{{ title }}</h2>
      <form class="archive-search-modal-form" @submit.prevent="handleSubmit">
        <div class="archive-search-modal-input-wrapper">
          <div class="archive-search-modal-icon">
            <svg
              v-if="isLoading"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="archive-search-modal-spinner"
            >
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
          </div>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            class="archive-search-modal-input"
            :placeholder="placeholder"
            autofocus
            autocomplete="off"
            @keydown.enter="handleKeydownEnter"
            @input="handleInput"
          />
        </div>
      </form>
      <div
        v-if="searchQuery.trim() && !isLoading && results.length === 0"
        class="archive-search-modal-empty"
      >
        No results found for "{{ searchQuery }}"
      </div>
      <div v-if="results.length > 0" class="archive-search-modal-results">
        <a
          v-for="result in results"
          :key="result.id"
          :href="result.absolute_url"
          class="archive-search-modal-result-item"
          @click="handleResultClick"
        >
          <div class="archive-search-modal-result-title">
            {{ result.subject }}
          </div>
          <div
            v-if="result.description"
            class="archive-search-modal-result-description"
          >
            {{ result.description }}
          </div>
          <div class="archive-search-modal-result-meta">
            {{ formatDate(result.publish_date || result.creation_date) }}
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from "vue";

interface PublicEmail {
  id: string;
  subject: string;
  slug: string;
  publish_date: string | null;
  creation_date: string;
  description: string | null;
  absolute_url: string;
}

interface Props {
  archiveUrl: string;
  placeholder?: string;
  searchParam?: string;
  title?: string;
  username?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Search archives...",
  searchParam: "q",
  title: "Search archives",
  username: "",
});

const emit = defineEmits<{
  close: [];
}>();

const isOpen = ref(false);
const searchQuery = ref("");
const searchInput = ref<HTMLInputElement | null>(null);
const results = ref<PublicEmail[]>([]);
const isLoading = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const open = () => {
  isOpen.value = true;
  nextTick(() => {
    searchInput.value?.focus();
  });
};

const close = () => {
  isOpen.value = false;
  searchQuery.value = "";
  results.value = [];
  isLoading.value = false;
  if (searchTimeout) {
    clearTimeout(searchTimeout);
    searchTimeout = null;
  }
  emit("close");
};

const handleInput = () => {
  // Clear existing timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  const trimmedQuery = searchQuery.value.trim();

  if (!trimmedQuery) {
    results.value = [];
    return;
  }

  // Debounce search
  searchTimeout = setTimeout(() => {
    performSearch(trimmedQuery);
  }, 300);
};

const performSearch = async (query: string) => {
  if (!props.username) {
    return;
  }

  isLoading.value = true;
  try {
    const apiUrl = `/v1/public/emails/${props.username}?q=${encodeURIComponent(
      query
    )}`;
    const response = await fetch(apiUrl);

    if (!response.ok) {
      throw new Error("Search failed");
    }

    const data = await response.json();
    results.value = data.results || [];
  } catch (error) {
    console.error("Search error:", error);
    results.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const handleKeydownEnter = (e: KeyboardEvent) => {
  e.preventDefault();
  if (results.value.length > 0) {
    // Navigate to first result if available
    window.location.href = results.value[0].absolute_url;
  } else {
    handleSubmit();
  }
};

const handleResultClick = () => {
  close();
};

const handleSubmit = () => {
  const trimmedQuery = searchQuery.value.trim();
  if (!trimmedQuery) {
    return;
  }

  // Handle both relative and absolute URLs
  let url: URL;
  try {
    // Try to parse as absolute URL first
    url = new URL(props.archiveUrl);
  } catch {
    // If that fails, treat as relative URL
    const baseUrl = props.archiveUrl.startsWith("/")
      ? `${window.location.origin}${props.archiveUrl}`
      : `${window.location.origin}/${props.archiveUrl}`;
    url = new URL(baseUrl);
  }

  // Set the search parameter (this will replace any existing value)
  url.searchParams.set(props.searchParam, trimmedQuery);

  // Navigate to the archive page with search results
  window.location.href = url.toString();
};

const handleEscape = (e: KeyboardEvent) => {
  if (e.key === "Escape" && isOpen.value) {
    close();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
});

defineExpose({
  open,
  close,
});
</script>

<style scoped>
.archive-search-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: fadeIn 0.25s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.archive-search-modal-content {
  background-color: var(--token-color-background, #ffffff);
  border-radius: 8px;
  border: 1px solid var(--token-color-border, #e5e7eb);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 100%;
  animation: slideUp 0.25s ease-out;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.archive-search-modal-form {
  display: flex;
  flex-direction: column;
}

.archive-search-modal-input-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.archive-search-modal-icon {
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--token-color-subtitle, #6b7280);
  pointer-events: none;
}

.archive-search-modal-input {
  width: 100%;
  height: 48px;
  padding: 0 14px 0 42px;
  border: 0;
  background-color: transparent;
  font-size: 14px;
  color: var(--token-color-title, #111827);
  outline: none;
}

.archive-search-modal-input::placeholder {
  color: var(--token-color-subtitle, #6b7280);
}

.archive-search-modal-input:focus {
  outline: none;
}

.archive-search-modal-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.archive-search-modal-empty {
  padding: 24px;
  text-align: center;
  color: var(--token-color-subtitle, #6b7280);
  font-size: 14px;
}

.archive-search-modal-results {
  max-height: 400px;
  overflow-y: auto;
  border-top: 1px solid var(--token-color-border, #e5e7eb);
}

.archive-search-modal-result-item {
  display: block;
  padding: 16px;
  border-bottom: 1px solid var(--token-color-border, #e5e7eb);
  text-decoration: none;
  color: inherit;
  transition: background-color 0.15s;
}

.archive-search-modal-result-item:hover {
  background-color: var(--token-color-background-offset, #f9fafb);
}

.archive-search-modal-result-item:last-child {
  border-bottom: none;
}

.archive-search-modal-result-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--token-color-title, #111827);
  margin-bottom: 4px;
}

.archive-search-modal-result-description {
  font-size: 13px;
  color: var(--token-color-subtitle, #6b7280);
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.archive-search-modal-result-meta {
  font-size: 12px;
  color: var(--token-color-subtitle, #9ca3af);
}
</style>
